import type { SxProps } from '@mui/material';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { Box } from 'hephaestus/web';
import { useMemo } from 'react';
import type { ModelOption } from '../utils/portfolioFilters';
import {
  createCompactModelFilterSx,
  MODEL_FILTER_POPPER_SX,
  MODEL_FILTER_WRAPPER_SX,
} from './portfolioFilters.styles';

export type ModelFilterSelectProps = {
  value: string[];
  preloadedModelOptions: ModelOption[];
  onSearchModels: (query: string) => Promise<ModelOption[]>;
  onChange: (ids: string[], options: ModelOption[]) => void;
  placeholder: string;
  loading?: boolean;
  sx?: SxProps;
};

export const ModelFilterSelect = ({
  value,
  preloadedModelOptions,
  onSearchModels,
  onChange,
  placeholder,
  loading = false,
  sx,
}: ModelFilterSelectProps) => {
  const hasSelection = value.length > 0;

  const optionById = useMemo(
    () => new Map(preloadedModelOptions.map((option) => [option.id, option])),
    [preloadedModelOptions],
  );

  const selected = useMemo(
    () => value.map((id) => optionById.get(id) ?? { id, name: id }),
    [value, optionById],
  );

  return (
    <Box sx={MODEL_FILTER_WRAPPER_SX}>
      <Autocomplete
        label=''
        multiple
        options={preloadedModelOptions}
        onSearch={onSearchModels}
        searchOnOpen
        minChars={0}
        value={selected}
        loading={loading}
        onChange={(_: unknown, newValue: ModelOption[] | null) => {
          const nextValue = newValue ?? [];
          onChange(
            nextValue.map((option) => option.id),
            nextValue,
          );
        }}
        getOptionLabel={(option: ModelOption) => option.name}
        isOptionEqualToValue={(a: ModelOption, b: ModelOption) => a.id === b.id}
        showSearchIcon
        size='small'
        disableCloseOnSelect
        fullWidth
        disableClearable={false}
        placeholder={hasSelection ? undefined : placeholder}
        testId='portfolio-filters-model'
        sx={{
          ...createCompactModelFilterSx(),
          ...sx,
        }}
        componentsProps={{
          popper: {
            sx: MODEL_FILTER_POPPER_SX,
          },
        }}
        ChipProps={{
          size: 'small',
        }}
      />
    </Box>
  );
};
